/**
 * Typed permission key constants.
 *
 * Every permission string used in the UI must come from this file.
 * Renaming a key here surfaces every call-site at compile time.
 *
 * Gate UI on leaf keys ("ari.view"), never on "x.*" or on a bare parent
 * resource ("ari.template"). Roles are granted leaves, so the matcher — which
 * expands the *requested* key's prefixes against the grant set — default-denies
 * anything coarser. The "x.*" keys below exist because some roles are seeded
 * with wildcard grants and denies; they are not gates.
 */
export const P = {
  Home: {
    All: "home.*",
    View: "home.view",
    ActionCenter: {
      All: "home.action_center.*",
      CreatePromo: "home.action_center.create_promo",
    },
  },
  Promotions: {
    All: "promo.*",
    View: "promo.view",
    Manage: "promo.manage",
  },
  Ari: {
    All: "ari.*",
    View: "ari.view",
    Manage: "ari.manage",
    StopSell: "ari.stop_sell",
    Template: {
      All: "ari.template.*",
      Download: "ari.template.download",
      Upload: "ari.template.upload",
    },
  },
  Reservations: {
    All: "res_report.*",
    View: "res_report.view",
    Edit: "res_report.edit",
    Download: "res_report.download",
    VccRepush: "res_report.vcc_repush",
    PmsId: {
      All: "res_report.pms_id.*",
      Upload: "res_report.pms_id.upload",
    },
  },
  PriceCheck: {
    All: "price_check.*",
    Search: "price_check.search",
    Export: "price_check.export",
  },
  AnalyticsHub: {
    // No leaf key exists: the matrix grants only "analytics_hub.*", so this
    // wildcard is the gate. A bare "analytics_hub" would never match — the
    // matcher's prefix loop skips single-segment keys.
    All: "analytics_hub.*",
  },
  Account: {
    All: "account.*",
    View: "account.view",
  },
  Team: {
    All: "team.*",
    View: "team.view",
  },
  User: {
    All: "user.*",
    // ViewProfile: "user.view_profile", // Note: user.view_profile is not sufficient to view users in Manage Users; team.view is also required
    EditProfile: "user.edit_profile",
    EditSelfProfile: "user.edit_self_profile",
    ManageSuperAdmin: "user.manage_super_admin",
    ManageAdmin: "user.manage_admin",
    ManageLiteUser: "user.manage_lite_user",
  },
  PropertyConfig: {
    All: "property_config.*",
    View: "property_config.view",
  },
  UserActivity: {
    All: "user_activity.*",
    View: "user_activity.view",
  },
};

/**
 * Maps user.manage_* permission keys to the role names they control.
 * Used to determine which users' Edit buttons are visible in Manage Users.
 */
export const MANAGE_PERMISSION_ROLE_MAP = {
  [P.User.ManageSuperAdmin]: ["PROPERTY_SUPER_ADMIN", "SUPER_ADMIN"],
  [P.User.ManageAdmin]: ["PROPERTY_ADMIN", "ADMIN"],
  [P.User.ManageLiteUser]: [
    "PROPERTY_REVENUE_MANAGER",
    "PROPERTY_FINANCE",
    "PROPERTY_VIEWER",
  ],
};

/**
 * Ordered hierarchy of manage permissions (lowest to highest privilege).
 * Denying a lower-level permission implicitly blocks all higher levels.
 */
export const MANAGE_PERMISSION_HIERARCHY = [
  P.User.ManageLiteUser,
  P.User.ManageAdmin,
  P.User.ManageSuperAdmin,
];

/**
 * Whether the caller may reach the add/edit user form at all.
 *
 * "Manage" spans add + edit, so it takes user.edit_profile *and* at least one
 * user.manage_* grant — without one there is no role the caller could assign or
 * any user they could edit. Mirrors the gate @hoteltradergit/user-management
 * puts on its "Add User" button (its internal resolveNavGates.canAddUser, which
 * the package does not export), so the /account/add-user route and the button
 * that leads to it agree.
 *
 * @param {(permKey: string) => boolean} hasPermission
 * @returns {boolean}
 */
export const canManageAnyUser = (hasPermission) =>
  hasPermission(P.User.EditProfile) &&
  MANAGE_PERMISSION_HIERARCHY.some((permKey) => hasPermission(permKey));
